/**
 * API 호출.
 *
 * 서버는 **에러 코드만** 준다. 사용자 문구는 여기서 현재 언어로 만든다 —
 * 글로벌 서비스라 서버가 문구를 만들면 언어 추가가 서버 재배포가 되고,
 * 클라이언트는 어차피 UI 번역을 갖고 있어 표가 두 벌이 된다(계획 §4).
 *
 * 경로는 항상 `/api/...`다. 배포에서는 CloudFront가 API Gateway로 보내고,
 * 로컬에서는 Vite 프록시가 보낸다 — **코드에 환경 분기가 없다.**
 */

import { MAX_EVENTS, MAX_EVENTS_PER_YEAR, MAX_TOPICS } from '@lifetimeline/shared';

import type { TranslationKey } from '../i18n/index.js';

const TOKEN_STORAGE_KEY = 'lifetimeline.token';

/** 서버가 주는 에러 응답 */
export interface ApiErrorBody {
  code: number;
  /** 개발자용. 사용자에게 그대로 보여주지 않는다 */
  reason: string;
  requestId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: number;
  readonly requestId: string | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(`API ${status} (${body.code}): ${body.reason}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.requestId = body.requestId;
  }
}

/** 네트워크가 끊겼거나 응답이 JSON이 아닐 때 */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('network request failed');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

// ──────────────────────────────────────────────────────────────
// 토큰
// ──────────────────────────────────────────────────────────────

/**
 * 토큰을 LocalStorage에 둔다.
 *
 * ⚠️ 트레이드오프: XSS가 나면 토큰이 털린다. HttpOnly 쿠키가 그 점에서는
 * 안전하지만, 쿠키를 쓰면 CSRF 방어가 필요하고 API Gateway·CloudFront에
 * 쿠키 전달 설정이 붙는다. 이 서비스는 **공개 프로필이 전부라 유출 시 피해가
 * 제한적**이고(남의 Pick을 고칠 수 있는 정도), 구조를 단순하게 두는 편이 낫다고
 * 판단했다. 결제나 개인정보가 생기면 쿠키 방식으로 옮긴다.
 */
export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // 저장이 안 되면 이번 세션만 유지된다
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // 무시
  }
}

// ──────────────────────────────────────────────────────────────
// 요청
// ──────────────────────────────────────────────────────────────

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** 토큰을 붙이지 않는다. 공개 조회에 쓴다 */
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, anonymous = false, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (!anonymous) {
    const token = readToken();
    if (token !== null) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (cause) {
    // 중단은 에러가 아니다 — 호출부가 구분할 수 있게 그대로 올린다
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw cause;
    }
    throw new NetworkError(cause);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch (cause) {
      // CloudFront 설정이 잘못되면 API 404가 index.html로 바뀌어 온다.
      // 그때 여기서 잡히므로 원인을 알 수 있게 NetworkError로 올린다.
      throw new NetworkError(cause);
    }
  }

  if (!response.ok) {
    const errorBody = parsed as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      errorBody ?? { code: response.status, reason: 'unknown error' },
    );
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

// ──────────────────────────────────────────────────────────────
// 에러 코드 → 문구
// ──────────────────────────────────────────────────────────────

/**
 * 서버 에러 코드를 문구 키로 옮긴다.
 *
 * 여기 없는 코드는 일반 문구로 떨어진다 — 서버가 새 코드를 내보내도 화면이
 * 깨지지 않는다. 대신 **새 코드를 추가하면 여기도 채워야** 안내가 구체적이다.
 *
 * 코드 값은 `api/src/support/error/customErrorCode.ts`의 정본을 따른다.
 */
const ERROR_MESSAGE_KEYS: Record<number, TranslationKey> = {
  404: 'error.notFound',
  1000: 'error.validation',
  1001: 'error.loginRequired',
  1003: 'error.sessionExpired',
  1005: 'error.tooManyRequests',
  1006: 'error.serverConfig',
  2000: 'error.notFound',
  2001: 'error.userIdTaken',
  2002: 'error.userAlreadyExists',
  2003: 'signUp.userIdReserved',
  2004: 'signUp.userIdInvalid',
  2005: 'error.loginRequired',
  4000: 'error.categoryNotFound',
  4001: 'error.categoryNotPickable',
  4002: 'error.categoryDeprecated',
  5000: 'error.imageNotUploaded',
  /*
   * 상한에 닿은 것은 **고장이 아니다.** 기본 문구("문제가 생겼어요")로
   * 떨어지면 사람은 자기가 뭘 잘못했는지 찾게 된다. 화면이 미리 막아 주는
   * 길(플랜 표)도 있지만, 다른 기기에서 먼저 채운 경우처럼 서버가 거절하는
   * 순간은 남는다.
   */
  6001: 'error.topicLimit',
  6003: 'error.eventLimit',
  6006: 'error.eventYearLimit',
};

/**
 * 상한 문구에 끼워 넣을 숫자.
 *
 * 문구에 "90"을 손으로 적으면 상한을 고치는 날 거짓말이 된다. 모르는
 * 자리는 `t`가 그냥 두므로 **어느 오류에 붙여도 해롭지 않다.**
 */
export const LIMIT_VALUES = {
  topics: MAX_TOPICS,
  events: MAX_EVENTS,
  perYear: MAX_EVENTS_PER_YEAR,
};

/** 예외를 사용자에게 보여줄 문구 키로 바꾼다. */
export function errorMessageKey(error: unknown): TranslationKey {
  if (error instanceof NetworkError) {
    return 'error.network';
  }
  if (error instanceof ApiError) {
    return ERROR_MESSAGE_KEYS[error.code] ?? 'error.generic';
  }
  return 'error.generic';
}

/** 토큰이 만료·위조됐는가. 로그인 화면으로 보낼지 판단한다. */
export function isAuthError(error: unknown): boolean {
  return error instanceof ApiError && (error.code === 1001 || error.code === 1003);
}
